import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  ChangeDetectorRef,
  DestroyRef,
  signal,
  AfterViewInit,
  viewChild
} from '@angular/core';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { EditorEditComponent } from '../editor-edit/editor-edit.component';
import { MatDialog } from '@angular/material/dialog';
import { AsyncPipe, DatePipe } from '@angular/common';
import { MatTooltip } from '@angular/material/tooltip';
import { ConfirmDialogComponent } from '../../core/components/confirm-delete-dialog/confirm-dialog.component';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { EditorRole } from '../editor.role.enum';
import { select, Store } from '@ngxs/store';
import { AppSelectors } from '../../app.state';
import { FreeFilterInputComponent } from '../../utils/component/free-filter-input/free-filter-input.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { LocalStorageService } from '../../core/services/local-storage.service';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { TableLoadingSpinnerComponent } from '../../utils/component/table-loading-spinner/table-loading-spinner';
import { EditorEntityService } from '../services/editor.entity.service';
import { EditorEntity } from '../editor.entity';
import { IFilters } from '@datx/jsonapi/interfaces/IFilters';
import { Observable, BehaviorSubject, combineLatest, from, map, switchMap, tap, firstValueFrom } from 'rxjs';
import { ConsumerEntity } from '../../consumer/consumer.entity';
import { ConsumerEntityService } from '../../consumer/services/consumer.entity.service';
import { WidgetMessageService } from '../../core/services/widget-message.service';
import { SubjectEntityService } from '../../subject/services/subject.entity.service';
import { SubjectEntity } from '../../subject/subject.entity';
import { EntityStateModel, InvalidateLoadedQueries } from '../../core/state/entity.state';
import { makeQueryKey } from '../../utils/entity-states/query-key';
import { SimpleConfirmData } from '../../core/components/confirm-dialog-raw/confirm-dialog-raw.component';
import { BaseFilterComponent } from '../../utils/component/base-filter-component/base-filter-component';

@Component({
  selector: 'pixl-main-editor',
  imports: [
    MatIcon,
    MatSortModule,
    MatIconModule,
    MatTableModule,
    MatButtonModule,
    DatePipe,
    MatTooltip,
    FreeFilterInputComponent,
    MatFormFieldModule,
    MatSelectModule,
    TranslocoModule,
    MatInputModule,
    MatPaginator,
    AsyncPipe,
    TableLoadingSpinnerComponent,
  ],
  templateUrl: './main-editor.component.html',
  styleUrl: './main-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class MainEditorComponent extends BaseFilterComponent implements OnInit, AfterViewInit {
  sort = viewChild(MatSort);

  totalItems = 1000;

  private readonly store = inject(Store);
  private editorService = inject(EditorEntityService);
  private consumerService = inject(ConsumerEntityService);
  private subjectService = inject(SubjectEntityService);
  private widgetService = inject(WidgetMessageService);

  private dialog = inject(MatDialog);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private _liveAnnouncer = inject(LiveAnnouncer);
  private translocoService = inject(TranslocoService);
  private localStorageService = inject(LocalStorageService);

  editors$!: Observable<EditorEntity[]>;
  consumers$!: Observable<ConsumerEntity[]>;
  subjects$!: Observable<SubjectEntity[]>;

  deletedIds$ = new BehaviorSubject<string[]>([]);
  public refresh$ = new BehaviorSubject<void>(undefined);
  private readonly pageChange$ = toObservable(this.pageChange);

  public authState = select(AppSelectors.authState);

  public dataSource = new MatTableDataSource<EditorEntity>([] as EditorEntity[]);

  public displayedColumns: string[] = [
    'edit-actions',
    'id',
    'email',
    'role',
    'updated',
  ];

  public filters: IFilters = {};
  private sortString = 'id';

  public searchFilterHint = signal(false);

  public readonly coloredButtonsEnabled =
    this.localStorageService.coloredButtonsEnabled;

  ngOnInit() {
    this.loadRelationData();

    this.editors$ = combineLatest([
      this.pageChange$,
      this.refresh$,
      this.deletedIds$
    ]).pipe(
      switchMap(([{ pageIndex, pageSize }, , deletedIds]) => {
        const query: Record<string, string | number> = {
          'include': 'consumers,subjectsRel',
          'page[size]': pageSize,
          'page[number]': pageIndex + 1,
          'sort': this.sortString,
        };

        if (this.freeFilter?.trim()?.length >= this.freeFilterLen) {
          query['filter[idOrEmailLike]'] = this.freeFilter.trim();
        }

        if (this.roleFilter?.length > 0) {
          query['filter[roles]'] = this.roleFilter.join(',');
        }

        return from(this.editorService.ensureLoaded(query)).pipe(
          switchMap(() => this.store.select(this.editorService.allByQuery(query))),
          map((entities: EditorEntity[]) => entities.filter(m => !deletedIds.includes(m.id))),
          tap(() => {
            const state = this.store.selectSnapshot(this.editorService.stateClass) as EntityStateModel<EditorEntity>;
            const queryKey = makeQueryKey(query);
            this.totalItems = state?.meta?.[queryKey]?.page?.total ?? 0;
            this.cdr.markForCheck();
          })
        );
      })
    );
  }

  ngAfterViewInit(): void {
    this.paginator()?.page.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event: PageEvent) => {
      this.pageChange.set({ pageIndex: event.pageIndex, pageSize: event.pageSize });
    });
  }

  private loadRelationData(): void {
    const consumerQuery = { sort: 'name' };
    this.consumers$ = from(this.consumerService.ensureLoaded(consumerQuery)).pipe(
      switchMap(() => this.store.select(this.consumerService.allByQuery(consumerQuery)))
    );

    const subjectQuery = { sort: 'name' };
    this.subjects$ = from(this.subjectService.ensureLoaded(subjectQuery)).pipe(
      switchMap(() => this.store.select(this.subjectService.allByQuery(subjectQuery)))
    );
  }

  addEditor(): void {
    const dialogRef = this.dialog.open(EditorEditComponent, {
      width: '2000px',
      maxWidth: '40vw',
    });

    dialogRef.afterClosed().subscribe(async (result: Partial<EditorEntity> | undefined) => {
      if (result) {
        const newEntityInstance = Object.assign(new EditorEntity(), result);
        await this.editorService.create(newEntityInstance);
        this.store.dispatch(new InvalidateLoadedQueries());
        this.refresh$.next();

        this.widgetService.setMessage({
          message: this.translocoService.translate('user_created'),
          messageTye: 'mtSuccess'
        });
      }
    });
  }

  public async deleteEditor(editor: EditorEntity): Promise<void> {
    if (!editor?.id) return;
    this.currentSelectedRow.set(editor.id);

    const confirmed = await firstValueFrom(
      this.dialog.open<ConfirmDialogComponent, SimpleConfirmData, boolean>(ConfirmDialogComponent, {
        data: {
          name: String(editor.email),
          headerText: this.translocoService.translate('delete_user'),
        }
      }).afterClosed()
    );

    if (!confirmed) return;

    await firstValueFrom(this.editorService.delete(editor.id));
    this.store.dispatch(new InvalidateLoadedQueries());
    this.refresh$.next();

    this.widgetService.setMessage({
      message: this.translocoService.translate('user_deleted'),
      messageTye: 'mtSuccess'
    });
  }

  editEditor(editor: EditorEntity): void {
    this.currentSelectedRow.set(editor.id);
    const dialogRef = this.dialog.open(EditorEditComponent, {
      width: '2000px',
      maxWidth: '40vw',
      data: {
        ...editor
      },
    });

    dialogRef.afterClosed().subscribe(async (updated: EditorEntity | undefined) => {
      if (!updated) return;
      await firstValueFrom(this.editorService.update(updated));
      this.refresh$.next();

      this.widgetService.setMessage({
        message: this.translocoService.translate('user_edited'),
        messageTye: 'mtSuccess'
      });
    });
  }

  public announceSortChange(sortState: Sort) {
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    }
    else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
    this.sortData(sortState);
  }

  public sortData(sort: Sort): void {
    this.sortString = sort.direction === 'desc' ? `-${sort.active}` : sort.active;
    this.refresh$.next();
  }

  public getCreatedDate(id: string): Date {
    return new Date(parseInt(id.substring(0, 8), 16) * 1000);
  }

  getConsumer(consumerId: string, getName = false): ConsumerEntity | string {
    if (!consumerId) return '---';
    const state = this.store.selectSnapshot(this.consumerService.stateClass);
    const consumer = this.consumerService.stateClass.byId(state)(consumerId);
    return getName ? (consumer?.name || consumerId) : consumer;
  }

  getSubject(subjectId: string, getName = false): SubjectEntity | string {
    if (!subjectId) return '---';
    const state = this.store.selectSnapshot(this.subjectService.stateClass);
    const subject = this.subjectService.stateClass.byId(state)(subjectId);
    return getName ? (subject?.name || subjectId) : subject;
  }

  isValidDate(dateString: string | Date): boolean {
    if (!dateString) {
      return false; // Kein Datum vorhanden
    }
    const date = new Date(dateString);
    // Überprüft, ob das Datum gültig ist und nicht 'Invalid Date' darstellt
    return date instanceof Date && !isNaN(date.getTime());
  }

  public getEditorRole(role: EditorRole): string {
    return this.translocoService.translate(role);
  }
}
